import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { MenuProps, SelectChangeEvent } from '@mui/material';
import { vehicleFormHooks } from 'app-components/flux/vehicle/vehicleForm';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { vehicleSelectors, vehicleThunks } from 'app-components/flux/vehicle';

import { type Account, accountsSelectors } from 'app-components/flux/accounts';
import type { KbbVehicleMake, KbbVehicleModel, KbbVehicleTrim } from 'app-components/flux/vehicle/vehicleTypes';
import QAmountField from 'components/QAmountField';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { VEHICLE_YEARS } from './utils';

const useStyles = makeStyles()((theme) => ({
  select: {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(10px, 8px) scale(0.8)',
      padding: 0,
    },
    '& .MuiSelect-select': {
      transform: 'translateY(9px)',
    },
    '& .MuiOutlinedInput-root': {
      background: theme.palette.background.default,
      border: `1px solid ${theme.palette.border.light}`,
      transition: 'border-color 0.15s ease',
      '& .MuiInputBase-input': {
        transform: 'translateY(9px)',
      },
      '&:not(.Mui-disabled):hover, &.Mui-focused': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        borderColor: theme.palette.border.light,
      },
    },
    '& .MuiInputLabel-root': {
      transition: 'color 0.15s ease, transform 0.15s ease',
    },
    '&:has(.MuiOutlinedInput-root:not(.Mui-disabled)):hover .MuiInputLabel-root': {
      color: theme.palette.primary.main,
    },
  },
  inputLabel: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  inputLabelText: {
    background: theme.palette.background.default,
    padding: theme.spacing(0, 0.75),
  },
  nameInput: {
    '& input': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  textInput: {
    '& .MuiInputLabel-root': {
      transform: 'translate(20px, 17px) scale(1)',
      transition: 'color 0.15s ease, transform 0.15s ease',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(15px, 8px) scale(0.8)',
    },
  },
}));

const LOADING_SPINNER = () => <CircularProgress size={20} sx={{ ml: 'auto', mr: 4 }} />;

const MENU_PROPS: Partial<MenuProps> = {
  slotProps: { paper: { sx: { maxHeight: 400 } } },
};

type NameInputProps = { linkTo?: undefined | string };

export const NameInput = React.memo(function NameInput(props: NameInputProps) {
  const { value: accountName, onValueChange: setAccountName, error, helperText } = vehicleFormHooks.useField('name');
  const { classes } = useStyles();
  const { linkTo } = props;

  const [uniqueNameError, setUniqueNameError] = useState<boolean>(false);

  const vehicleAccountsById = useAppSelector(accountsSelectors.getVehicleAccounts);
  const vehicleAccounts = vehicleAccountsById.toList();

  const handleNameChange = (name: string) => {
    setAccountName(name);
    const isDuplicateName = vehicleAccounts.some(
      (account: Account) => account.name?.toLowerCase() === name?.toLowerCase(),
    );
    setUniqueNameError(isDuplicateName);
  };

  const isDisabled = typeof linkTo === 'string' && linkTo !== '0';

  const renderHelperText = uniqueNameError ? 'Account name already exists' : helperText || ' ';

  return (
    <FormControl fullWidth>
      <TextField
        id='vehicle-account-name'
        label='Account Name'
        value={accountName}
        onChange={(e) => {
          const enteredName = e.target.value;
          handleNameChange(enteredName);
        }}
        className={classes.nameInput}
        slotProps={{ htmlInput: { maxLength: 86 } }}
        autoComplete='off'
        helperText={renderHelperText}
        disabled={isDisabled}
        FormHelperTextProps={{ error: !!error || uniqueNameError }}
        InputLabelProps={{ error: false }}
      />
    </FormControl>
  );
});

export const EstimateInput = React.memo(function EstimateInput() {
  const { value: estimate, onValueChange: setEstimate, error, helperText } = vehicleFormHooks.useField('estimate');

  return (
    <FormControl fullWidth>
      <QAmountField
        noNegatives
        editable
        id='vehicle-manual-estimate-input'
        label='Value'
        value={estimate}
        onChange={(e) => {
          const enteredEstimate = e.target.value;
          setEstimate(enteredEstimate);
        }}
        autoComplete='off'
        helperText={helperText || ' '}
        FormHelperTextProps={{ error: !!error }}
        InputLabelProps={{ error: false }}
      />
    </FormControl>
  );
});

export const VinInput = React.memo(function VinInput() {
  const { value, onValueChange: setVin, error, helperText } = vehicleFormHooks.useField('vin');
  const { classes } = useStyles();

  return (
    <FormControl fullWidth>
      <TextField
        id='vehicle-vin-input'
        label='17-digit VIN'
        className={classes.textInput}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const enteredVin = e.target.value;
          setVin(enteredVin);
        }}
        autoComplete='off'
        helperText={helperText || ' '}
        FormHelperTextProps={{ error: !!error }}
        InputLabelProps={{ error: false }}
      />
    </FormControl>
  );
});

export const YearInput = React.memo(function YearInput() {
  const { classes } = useStyles();
  const reduxDispatch = useAppDispatch();

  const { value: currentYearValue, onValueChange: setYear, error, helperText } = vehicleFormHooks.useField('year');

  return (
    <FormControl fullWidth className={classes.select} error={!!error}>
      <InputLabel id='vehicle-year-label' className={classes.inputLabel} error={false}>
        <Typography className={classes.inputLabelText}>Year</Typography>
      </InputLabel>
      <Select
        id='vehicle-year-input'
        value={currentYearValue ?? ''}
        label='Year'
        labelId='vehicle-year-label'
        MenuProps={MENU_PROPS}
        onChange={(e: SelectChangeEvent<string>) => {
          const selectedYear = e.target.value;
          setYear(selectedYear);
          if (selectedYear) reduxDispatch(vehicleThunks.fetchKbbMakes(selectedYear));
        }}
      >
        {VEHICLE_YEARS().map((year) => (
          <MenuItem key={`vehicle-input-year-${year}`} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
});

export const MakeInput = React.memo(function MakeInput() {
  const { classes } = useStyles();
  const reduxDispatch = useAppDispatch();

  const { value: currentMake, onValueChange: setMake, error, helperText } = vehicleFormHooks.useField('make');
  const selectedYear = vehicleFormHooks.useSelector(({ state }) => state.year);

  const makeOptions = useAppSelector(vehicleSelectors.selectKbbMakeOptions);
  const areMakesLoading = useAppSelector(vehicleSelectors.getKbbMakesLoading);

  const isDisabled = areMakesLoading || !selectedYear || makeOptions.length === 0;

  return (
    <FormControl fullWidth className={classes.select} error={!!error} disabled={isDisabled}>
      <InputLabel id='vehicle-make-label' className={classes.inputLabel} error={false}>
        <Typography className={classes.inputLabelText}>Make</Typography>
        {areMakesLoading && <LOADING_SPINNER />}
      </InputLabel>
      <Select
        id='vehicle-make-input'
        value={currentMake?.makeId ?? ''}
        label='Make'
        labelId='vehicle-make-label'
        MenuProps={MENU_PROPS}
        onChange={(e: SelectChangeEvent<number>) => {
          const selectedMakeId = Number(e.target.value);
          const selectedMake = makeOptions?.find((make) => make.makeId === selectedMakeId);

          if (selectedMake) {
            setMake({ makeId: selectedMake.makeId, makeName: selectedMake.makeName });
          }
          if (selectedYear && selectedMake) {
            reduxDispatch(vehicleThunks.fetchKbbModels({ year: selectedYear, makeId: selectedMake.makeId }));
          }
        }}
      >
        {makeOptions?.map((make: KbbVehicleMake) => (
          <MenuItem key={`vehicle-input-${make.makeId}`} value={make.makeId}>
            {make.makeName}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
});

export const ModelInput = React.memo(function ModelInput() {
  const { classes } = useStyles();
  const reduxDispatch = useAppDispatch();

  const { value: currentModel, onValueChange: setModel, error, helperText } = vehicleFormHooks.useField('model');
  const selectedYear = vehicleFormHooks.useSelector(({ state }) => state.year);
  const selectedMake = vehicleFormHooks.useSelector(({ state }) => state.make);

  const modelOptions = useAppSelector(vehicleSelectors.selectKbbModelOptions);
  const areModelsLoading = useAppSelector(vehicleSelectors.getKbbModelsLoading);

  const isDisabled = areModelsLoading || !selectedYear || !selectedMake || modelOptions.length === 0;

  return (
    <FormControl fullWidth className={classes.select} error={!!error} disabled={isDisabled}>
      <InputLabel id='vehicle-model-label' className={classes.inputLabel} error={false}>
        <Typography className={classes.inputLabelText}>Model</Typography>
        {areModelsLoading && <LOADING_SPINNER />}
      </InputLabel>
      <Select
        id='vehicle-model-input'
        value={currentModel?.modelId ?? ''}
        label='Model'
        labelId='vehicle-model-label'
        MenuProps={MENU_PROPS}
        onChange={(e: SelectChangeEvent<number>) => {
          const selectedModelId = Number(e.target.value);
          const selectedModel = modelOptions?.find((modelOptions) => modelOptions.modelId === selectedModelId);

          if (selectedModel) {
            setModel({ modelId: selectedModel.modelId, modelName: selectedModel.modelName });
          }
          if (selectedYear && selectedMake && selectedModelId) {
            reduxDispatch(
              vehicleThunks.fetchKbbTrims({
                year: selectedYear,
                makeId: selectedMake.makeId,
                modelId: selectedModelId,
              }),
            );
          }
        }}
      >
        {modelOptions?.map((model: KbbVehicleModel) => (
          <MenuItem key={`vehicle-input-${model.modelId}`} value={model.modelId}>
            {model.modelName}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
});

export const TrimInput = React.memo(function TrimInput() {
  const { classes } = useStyles();

  const { value: currentTrim, onValueChange: setTrim, error, helperText } = vehicleFormHooks.useField('trim');
  const selectedYear = vehicleFormHooks.useSelector(({ state }) => state.year);

  const trimOptions = useAppSelector(vehicleSelectors.selectKbbTrimOptions);
  const areTrimsLoading = useAppSelector(vehicleSelectors.getKbbTrimsLoading);

  const isDisabled = areTrimsLoading || !selectedYear || trimOptions.length === 0;

  return (
    <FormControl fullWidth className={classes.select} error={!!error} disabled={isDisabled}>
      <InputLabel id='vehicle-trim-label' className={classes.inputLabel} error={false}>
        <Typography className={classes.inputLabelText}>Trim</Typography>
        {areTrimsLoading && <LOADING_SPINNER />}
      </InputLabel>
      <Select
        id='vehicle-trim-input'
        value={currentTrim?.trimId ?? ''}
        label='Trim'
        labelId='vehicle-trim-label'
        MenuProps={MENU_PROPS}
        onChange={(e: SelectChangeEvent<number>) => {
          const selectedTrimId = Number(e.target.value);
          const selectedTrim = trimOptions.find((trim) => trim.trimId === selectedTrimId);

          if (selectedTrim) {
            setTrim({
              trimId: selectedTrim.trimId,
              trimName: selectedTrim.trimName,
              modelYearId: selectedTrim.modelYearId,
            });
          }
        }}
      >
        {trimOptions?.map((trim: KbbVehicleTrim) => (
          <MenuItem key={`vehicle-input-${trim.trimId}`} value={trim.trimId}>
            {trim.trimName}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText || ' '}</FormHelperText>
    </FormControl>
  );
});

export const MilesInput = React.memo(function MilesInput() {
  const { value: currentMilesValue, onValueChange: setMiles, error, helperText } = vehicleFormHooks.useField('miles');
  const { classes } = useStyles();

  return (
    <FormControl fullWidth>
      <TextField
        id='vehicle-miles-input'
        label='Miles'
        className={classes.textInput}
        value={currentMilesValue ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const enteredMiles = e.target.value;
          setMiles(enteredMiles);
        }}
        autoComplete='off'
        helperText={helperText || ' '}
        FormHelperTextProps={{ error: !!error }}
        InputLabelProps={{ error: false }}
      />
    </FormControl>
  );
});

export const ZipCodeInput = React.memo(function ZipCodeInput() {
  const { value: currentZipCode, onValueChange: setZipCode, error, helperText } = vehicleFormHooks.useField('zipCode');
  const { classes } = useStyles();

  return (
    <FormControl fullWidth>
      <TextField
        id='vehicle-zipCode-input'
        label='ZIP Code'
        className={classes.textInput}
        value={currentZipCode ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const enteredZipCode = e.target.value;
          setZipCode(enteredZipCode);
        }}
        autoComplete='off'
        helperText={helperText || ' '}
        FormHelperTextProps={{ error: !!error }}
        InputLabelProps={{ error: false }}
      />
    </FormControl>
  );
});
